import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3300';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface PlanCreator {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface PlanRectangle {
  id: number;
  planId: number;
  parentId: number | null;
  title: string;
  content: string | null;
  color: string;
  positionX: number | null;
  positionY: number | null;
  width: number | null;
  height: number | null;
  linkedUserIds: number[];
  linkedTaskIds: number[];
  createdAt: string;
  updatedAt: string;
  parent?: PlanRectangle | null;
  children?: PlanRectangle[];
}

export interface Plan {
  id: number;
  name: string;
  description: string | null;
  createdById: number;
  createdBy?: PlanCreator;
  createdAt: string;
  updatedAt: string;
  rectangles?: PlanRectangle[];
  _count?: {
    rectangles: number;
  };
}

export interface CreatePlanDto {
  name: string;
  description?: string;
}

export interface UpdatePlanDto {
  name?: string;
  description?: string;
}

export interface CreateRectangleDto {
  title: string;
  content?: string;
  color?: string;
  parentId?: number;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  linkedUserIds?: number[];
  linkedTaskIds?: number[];
}

export interface UpdateRectangleDto extends Partial<CreateRectangleDto> {}

export interface RectangleTree {
  planId: number;
  planName: string;
  roots: PlanRectangle[];
  allRectangles: PlanRectangle[];
}

// Plans API
const planService = {
  // Plan CRUD
  getPlans: (): Promise<Plan[]> =>
    api.get('/plans').then((res) => res.data),

  getPlan: (id: number): Promise<Plan> =>
    api.get(`/plans/${id}`).then((res) => res.data),

  createPlan: (data: CreatePlanDto): Promise<Plan> =>
    api.post('/plans', data).then((res) => res.data),

  updatePlan: (id: number, data: UpdatePlanDto): Promise<Plan> =>
    api.patch(`/plans/${id}`, data).then((res) => res.data),

  deletePlan: (id: number): Promise<void> =>
    api.delete(`/plans/${id}`).then((res) => res.data),

  // Rectangle CRUD
  getRectangles: (planId: number): Promise<PlanRectangle[]> =>
    api.get(`/plans/${planId}/rectangles`).then((res) => res.data),

  getRectangleTree: (planId: number): Promise<RectangleTree> =>
    api.get(`/plans/${planId}/rectangles/tree`).then((res) => res.data),

  getRectangle: (planId: number, rectangleId: number): Promise<PlanRectangle> =>
    api.get(`/plans/${planId}/rectangles/${rectangleId}`).then((res) => res.data),

  createRectangle: (planId: number, data: CreateRectangleDto): Promise<PlanRectangle> =>
    api.post(`/plans/${planId}/rectangles`, data).then((res) => res.data),

  updateRectangle: (planId: number, rectangleId: number, data: UpdateRectangleDto): Promise<PlanRectangle> =>
    api.patch(`/plans/${planId}/rectangles/${rectangleId}`, data).then((res) => res.data),

  deleteRectangle: (planId: number, rectangleId: number): Promise<void> =>
    api.delete(`/plans/${planId}/rectangles/${rectangleId}`).then((res) => res.data),
};

export default planService;

